import {
  Badge,
  Button,
  ListItem,
  Popover,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import QrCodeIcon from '@material-ui/icons/CenterFocusStrong';
import QRCode from 'react-qr-code';
import { get } from 'lodash';
import React from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { useAppContext } from '../../config/AppContext';
import ChatIcon from '../../images/chat_btn.png';
import FanfoodLogo from '../../images/logo_red.png';
import { canChangeVenues } from '../../services/Auth.service';
import { getVenueWebLink } from '../../services/WebLinks.service';
import './Home.scss';

const Navbar = (): JSX.Element => {
  const [
    showNotifications,
    setShowNotifications,
  ] = React.useState<HTMLButtonElement | null>(null);
  const [qrModalOpen, setQrModalOpen] = React.useState(false);
  const [qrInput, setQrInput] = React.useState('');
  const [qrValue, setQrValue] = React.useState('');
  const [qrError, setQrError] = React.useState('');
  const [qrSize, setQrSize] = React.useState(256);
  const [fgColor, setFgColor] = React.useState('#000000');
  const [bgColor, setBgColor] = React.useState('#FFFFFF');
  const [isDownloading, setIsDownloading] = React.useState(false);

  const { unreadMessages, user, venue } = useAppContext();
  const history = useHistory();

  const isVenueChangeable = canChangeVenues(user);

  const handleOpenPopover = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const hasUnreadMessages: boolean = Object.keys(unreadMessages).length > 0;
    if (hasUnreadMessages) {
      setShowNotifications(get(event, 'currentTarget'));
    } else if (venue) {
      history.push(`/home/venue-details/${venue.id}/support`);
    }
  };

  const handleQrInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQrInput(v);
    if (!v.trim()) {
      setQrError('Please enter a valid URL or text');
      setQrValue('');
    } else {
      setQrError('');
      setQrValue(v.trim());
    }
  };

  const handleDownloadQr = () => {
    const svg = document.querySelector('.qr-code-container svg');
    if (!svg) return;

    setIsDownloading(true);

    // Create a canvas element to convert SVG to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    // Set canvas size with padding for better resolution
    const padding = 20;
    canvas.width = qrSize + padding * 2;
    canvas.height = qrSize + padding * 2;

    img.onload = () => {
      if (!ctx) return;

      // Draw background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw QR code
      ctx.drawImage(img, padding, padding, qrSize, qrSize);

      // Optionally add logo overlay
      const logo = new Image();
      logo.src = FanfoodLogo;
      logo.onload = () => {
        const logoSize = qrSize * 0.2; // Logo is 20% of QR code size
        const logoX = (canvas.width - logoSize) / 2;
        const logoY = (canvas.height - logoSize) / 2;
        ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

        // Create download link
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = 'qr-code.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setIsDownloading(false);
      };

      logo.onerror = () => {
        // Fallback if logo fails to load
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = 'qr-code.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setIsDownloading(false);
      };
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <>
      <div className="app-header">
        <div className="logo-venue">
          <img className="fanfood-logo" src={FanfoodLogo} alt="Fanfood" />
          {venue && (
            <div className="venue">
              {venue.name}
              {isVenueChangeable && venue.id && (
                <Button
                  className="btn-link"
                  component="button"
                  onClick={() => {
                    history.push('/home/venues');
                  }}
                >
                  Change
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="header-right">
          {venue && venue.active && (
            <Button
              className="btn-link"
              href={getVenueWebLink(venue.id)}
              target="_blank"
            >
              View Venue In Web
            </Button>
          )}
          <IconButton
            className="btn-link"
            onClick={() => setQrModalOpen(true)}
            aria-label="Open QR Code Generator"
          >
            <QrCodeIcon style={{ fontSize: 40 }} />
          </IconButton>

          {user && (
            <Typography classes={{ root: 'greeting-text' }}>
              Hi, {user.firstName}!
            </Typography>
          )}
          {venue && (
            <Badge
              badgeContent={Object.keys(unreadMessages).length}
              classes={{ badge: 'menu__chat__number' }}
              color="primary"
              invisible={Object.keys(unreadMessages).length === 0}
            >
              <Button
                classes={{
                  label: 'menu-button-label menu-button-chat-color',
                }}
                onClick={handleOpenPopover}
                aria-label={`Chat with ${
                  Object.keys(unreadMessages).length
                } unread messages`}
              >
                <div className="menu__chat">
                  <img className="chat-icon" src={ChatIcon} alt="chat-icon" />
                </div>
              </Button>
              <Popover
                anchorEl={showNotifications}
                anchorOrigin={{
                  horizontal: 'left',
                  vertical: 'bottom',
                }}
                open={Boolean(showNotifications)}
                onClose={() => setShowNotifications(null)}
              >
                {Object.values(unreadMessages).map(
                  (message: any, i: number) => (
                    <ListItem
                      onClick={() =>
                        history.push(
                          `/home/venue-details/${venue.id}/support`,
                          [{ storeId: get(message, 'storeId') }],
                        )
                      }
                      divider={true}
                      key={`${message}__${i}`}
                      button
                    >
                      <Typography className="menu__chat__messages" variant="h6">
                        {get(message, 'order')}: You have a new message
                      </Typography>
                    </ListItem>
                  ),
                )}
              </Popover>
            </Badge>
          )}
        </div>
      </div>

      <Dialog
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="qr-code-dialog-title"
      >
        <DialogTitle style={{ padding: '6px 28px' }} id="qr-code-dialog-title">
          Design Your QR Code
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <TextField
              label="URL or Text"
              fullWidth
              value={qrInput}
              onChange={handleQrInputChange}
              error={Boolean(qrError)}
              helperText={
                qrError || 'Enter a URL or text to generate a QR code'
              }
              variant="outlined"
              margin="normal"
              autoFocus
              inputProps={{ 'aria-label': 'QR code input' }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              mt={2}
            >
              <FormControl variant="outlined" style={{ width: '45%' }}>
                <InputLabel>QR Code Size</InputLabel>
                <Select
                  value={qrSize}
                  onChange={e => setQrSize(Number(e.target.value))}
                  label="QR Code Size"
                >
                  <MenuItem value={128}>Small (128x128)</MenuItem>
                  <MenuItem value={256}>Medium (256x256)</MenuItem>
                  <MenuItem value={512}>Large (512x512)</MenuItem>
                </Select>
              </FormControl>
              <Box display="flex">
                <TextField
                  label="Foreground Color"
                  type="color"
                  value={fgColor}
                  onChange={e => setFgColor(e.target.value)}
                  variant="outlined"
                  style={{ width: '100px' }}
                />
                <TextField
                  label="Background Color"
                  type="color"
                  value={bgColor}
                  onChange={e => setBgColor(e.target.value)}
                  variant="outlined"
                  style={{ width: '100px' }}
                />
              </Box>
            </Box>
            {qrValue && (
              <>
                <Box
                  display="flex"
                  justifyContent="center"
                  width="100%"
                  bgcolor={bgColor}
                  p={2}
                  marginTop={2}
                  borderRadius={2}
                  boxShadow={2}
                  style={{ border: '6px solid #fff', position: 'relative' }}
                  className="qr-code-container"
                >
                  <QRCode
                    value={qrValue}
                    size={qrSize}
                    level="H"
                    fgColor={fgColor}
                    bgColor={bgColor}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQrModalOpen(false);
              setQrInput('');
              setQrValue('');
              setQrError('');
              setQrSize(256);
              setFgColor('#000000');
              setBgColor('#FFFFFF');
            }}
            aria-label="Cancel QR code generation"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadQr}
            color="primary"
            disabled={!qrValue || isDownloading}
            startIcon={isDownloading ? <CircularProgress size={20} /> : null}
            aria-label="Download QR code as PNG"
          >
            {isDownloading ? 'Downloading...' : 'Download PNG'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withRouter(Navbar);
